import { LinkField, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import Button from 'components/helpers/Button';
import { useCart } from 'components/helpers/Cart/CartHelper';
import { CartItem } from 'lib/util/cart';
import SvgIcon from 'components/helpers/SvgIcon/SvgIcon';
import useDictionary from 'lib/sitecore/use-dictionary';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { classnames } from 'tailwindcss-classnames';
import {
  EcommerceDataLayerObject,
  CartErrorDataLayerObject,
  ProductItemRP,
  gtmDatalayerPush,
  gtmEcommerceDatalayerPush,
  alwaysSuppressLinkDataLayerPush,
  OnLinkDataLayerPushProps,
} from 'lib/util/gtm-datalayer';

const handleViewCartGtmPush = (pushProps: OnLinkDataLayerPushProps, cartItems: CartItem[]) => {
  const items = cartItems.map((item, i) => {
    return {
      item_id: item.itemNumber ?? item.id,
      item_name: item.name,
      item_list_name: 'order materials',
      index: i.toString(),
      price: item.price,
      quantity: item.quantity,
    };
  }) as ProductItemRP[];

  const gmtData: EcommerceDataLayerObject = {
    event: 'view_cart',
    ecommerce: {
      value: '0',
      currency: 'USD',
      items: items,
    },
  };
  gtmEcommerceDatalayerPush(gmtData);
  pushProps.preventDefault();
};
const handleContinueShoppingGtmPush = (pushProps: OnLinkDataLayerPushProps) => {
  const myDataLayer = pushProps.dataLayerInfo;
  if (!myDataLayer) {
    return;
  }
  myDataLayer.clickText = 'Continue Shopping';
  if (typeof window !== 'undefined') {
    myDataLayer.linkUrl = window.location.href;
  }
};
const getQuantityFromHash = (hash?: string): string | undefined => {
  const myHash = (hash ?? '').replace(/^#+/i, '').trim();
  if (myHash.length === 0) {
    return undefined;
  }
  const params = new URLSearchParams(myHash);
  return params.get('quantity') as string;
};
const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
  if (!/^\d$/.test(event.key) && event.key !== 'Backspace' && event.key !== 'Delete') {
    event.preventDefault();
  }
};
const getItemForCartPush = ({
  itemNumber,
  id,
  name,
  price,
  quantity,
}: {
  itemNumber?: string;
  id?: string;
  name?: string;
  price?: number;
  quantity: number;
}) => {
  return [
    {
      item_id: itemNumber ?? id,
      item_name: name,
      price,
      quantity,
      affiliation: 'N/A',
      coupon: 'N/A',
      discount: 'N/A',
      index: '0',
      item_brand: 'NMDP',
      item_list_name: 'order materials',
      item_variant: 'N/A',
    },
  ] as ProductItemRP[];
};

const AddToCart = ({
  name,
  id,
  price,
  itemNumber,
  itemMin,
  itemMaxUs,
  itemMaxIntl,
  quantity,
  image,
  productLink,
}: CartItem): React.JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const cartLink = sitecoreContext?.cartLink as LinkField;
  const dictionary = useDictionary();
  const { addToCart, validateQuantity, getQuantityLeftById, getMaxQuantity, fetchCart } = useCart();
  const [inputValue, setInputValue] = useState<number>(quantity < 1 ? 1 : quantity);
  const [inputValid, validateInputValue] = useState(true);
  const [maxAllowedvalue, setMaxAllowedVal] = useState(itemMaxIntl);
  const [isOpen, setIsOpen] = useState(false);
  const router = useRouter();
  const buttonRef = useRef<HTMLButtonElement>(null);

  // get the cart link and update the text to 'View cart'
  const viewCartLink = {
    ...cartLink?.value,
    text: 'View cart',
  };

  useEffect(() => {
    let timeoutId: undefined | number;
    // Check if router is ready and we are running client-side
    if (router.isReady && typeof window !== 'undefined') {
      // Grab check if we have a quantity in the hash
      const hashQuantity = getQuantityFromHash(window.location?.hash);
      if (hashQuantity) {
        // Update our input state
        setInputValue(parseInt(hashQuantity, 10));
        // Wait for the submit button to appear
        timeoutId = window.setInterval(() => {
          if (buttonRef.current) {
            // Found it - stop waiting and click it
            window.clearInterval(timeoutId);
            timeoutId = undefined;
            buttonRef.current.click();
            // Then remove our hash from the url
            window.history.replaceState(
              {},
              document.title,
              window.location.pathname + window.location.search
            );
          }
        }, 100);
      }
    }
    // Make sure to clear out any running intervals on unmount
    return () => {
      if (typeof window !== 'undefined' && timeoutId) {
        window.clearInterval(timeoutId);
        timeoutId = undefined;
      }
    };
  }, [router, setInputValue]);
  useEffect(() => {
    setMaxAllowedVal(getMaxQuantity(itemMaxUs as number, itemMaxIntl as number, id as string));
  }, [getMaxQuantity, itemMaxIntl, itemMaxUs, id]);
  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setInputValue(Number(event.target.value));
  const maxQuantity = itemMaxUs as number;
  const validationMessage = dictionary.Maximum_Order_Limit?.replace('{0}', maxQuantity?.toString());
  const handleIncrement = () => {
    setInputValue(inputValue + 1);
  };
  const handleDecrement = () => {
    if (inputValue > 0) {
      setInputValue(inputValue - 1);
    }
  };
  const handleAddToCart = () => {
    if (inputValue === 0) {
      return;
    }
    const validateItemQuantity = validateQuantity(
      {
        id,
        name,
        price,
        itemMin,
        itemNumber,
        itemMaxIntl,
        itemMaxUs,
        quantity: inputValue,
        image,
        productLink,
      },
      false
    );
    if (validateItemQuantity) {
      validateInputValue(true);
      addToCart({
        id,
        name,
        price,
        itemMin,
        itemNumber,
        itemMaxIntl,
        itemMaxUs,
        quantity: inputValue,
        image,
        productLink,
      });

      setMaxAllowedVal((maxAllowedvalue as number) - inputValue);
      gtmEcommerceDatalayerPush({
        event: 'add_to_cart',
        ecommerce: {
          currency: 'USD',
          value: '0',
          items: getItemForCartPush({ itemNumber, id, name, price, quantity: inputValue }),
        },
      } as EcommerceDataLayerObject);
      openModal();
      setInputValue(1);
    } else {
      validateInputValue(false);
      // set state of validation to false and perform GTM dataLayer push
      gtmDatalayerPush({
        event: 'e_cartError',
        productName: name,
        errorMessage: validationMessage,
      } as CartErrorDataLayerObject);
    }
  };
  const handleInputBlur = () => {
    const quantityLeft = getQuantityLeftById(id as string, inputValue);
    if (inputValue > quantityLeft) {
      setInputValue(quantityLeft);
    }
  };

  const isAddButtonDisabled =
    itemMaxIntl === undefined || inputValue >= (maxAllowedvalue as number);
  const isAddToCartButtonDisabled = inputValue < 1;
  return (
    <>
      <div className={classNames('order-1 flex w-full flex-wrap lg:w-auto')}>
        <div className={classNames(`quantity-selector-block${!inputValid ? ' red-border' : ''}`)}>
          <div className={classNames('')}>
            <button
              type="button"
              onClick={handleDecrement}
              disabled={inputValue <= 1 ? true : false}
              className={classNames('quantity-minus', `${inputValue <= 1 ? 'disabledButton' : ''}`)}
              aria-label="Remove One Item"
            >
              <SvgIcon
                className={classNames('text-white')}
                icon="quantity-minus"
                size="sm"
                viewBoxSize="0 0 24 24"
              />
            </button>
          </div>
          <input
            type="number"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            step="1"
            onKeyPress={handleKeyPress}
            aria-label="input-quantity"
            className={classNames(
              'w-[36px] border-b border-solid border-[#A9A9A9] text-center outline-0'
            )}
          />
          <div className={classNames('')}>
            <button
              type="button"
              className={classNames(
                'quantity-plus',
                `${isAddButtonDisabled ? 'disabledButton' : ''}`
              )}
              disabled={isAddButtonDisabled}
              onClick={handleIncrement}
              aria-label="Add One Item"
            >
              <SvgIcon
                className={classNames('text-white')}
                icon="quantity-plus"
                size="sm"
                viewBoxSize="0 0 24 24"
              />
            </button>
          </div>
        </div>

        <div className={classNames('inline-block w-[calc(100%-181px)] lg:w-auto lg:pr-[16px]')}>
          <Button
            onClick={handleAddToCart}
            onGtmPush={alwaysSuppressLinkDataLayerPush}
            text="Add to Cart"
            variant="primary"
            disabled={isAddToCartButtonDisabled}
            className={classNames(`h-[46px] w-full`)}
          />
          <button
            type="button"
            ref={buttonRef}
            onClick={handleAddToCart}
            className={classnames('hidden')}
          >
            Hidden button
          </button>
        </div>
      </div>
      {!inputValid && (
        <p
          className={classNames(
            'order-2 mt-[8px] w-full text-sm leading-2 text-red-destructive-shade lg:order-3'
          )}
        >
          <span className={classNames('error-icon')}>
            <SvgIcon
              className={classNames('text-white')}
              icon="error"
              size="xs"
              viewBoxSize="0 0 16 16"
            />
          </span>
          {validationMessage}
        </p>
      )}
      <div>
        {isOpen && (
          <div className={classNames('fixed z-50')}>
            <div
              className={classNames('modal-overlay fixed inset-0 z-[100] bg-black opacity-50')}
            />
            <div
              className={classNames(
                'modal-container tt-center fixed left-[50%] top-[50%] z-50 z-[10000] w-[90%] overflow-y-auto rounded-[12px] bg-white shadow-lg md:mx-auto md:w-1/2 lg:w-1/3'
              )}
            >
              <div className={classNames('modal-content relative px-6 py-4 text-left')}>
                <div className={classNames('absolute right-[16px] top-[8px]')}>
                  <Button
                    linkField={{ value: { href: '#', text: '' } }}
                    suppressLinkText
                    variant="none"
                    className={classNames('modal-close ml-auto rounded-full text-4xl text-black')}
                    onGtmPush={handleContinueShoppingGtmPush}
                    onClick={closeModal}
                  >
                    &times;
                  </Button>
                </div>
                <h2 className={classNames('mb-[5px] text-2xl font-medium leading-9')}>
                  {dictionary.Added_To_Cart}
                </h2>
                <p className={classNames('mb-[30px] text-body leading-4')}>{name}</p>
                <div>
                  <Button
                    linkField={{ value: { href: '#', text: 'Continue Shopping' } }}
                    variant="secondary"
                    className={classNames(
                      'mb-4 w-full border-2 py-[7px] text-center md:mr-[16px] md:w-auto'
                    )}
                    onGtmPush={handleContinueShoppingGtmPush}
                    onClick={closeModal}
                  />
                  {cartLink?.value?.href?.length && (
                    <Button
                      linkField={{ value: viewCartLink }}
                      variant="primary"
                      onGtmPush={(e) => handleViewCartGtmPush(e, fetchCart())}
                      className={classNames('mb-4 w-full border-2 py-[7px] text-center md:w-auto')}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
export default AddToCart;
